import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { uniqBy } from "lodash";
import { IEmbed } from "../../embeds/embedsV2/interface";
import {
  VideoPanel,
  PanelEmpty,
  PanelError,
  PanelSkeleton,
  StoryTile,
  IconStoryGlyph,
} from "./panels";
import type { PanelStatus } from "./panels";
import { usePortalPath } from "../../../../hooks/usePortalPath";

export interface Props {
  stories: Array<IEmbed>;
  count: number;
  status: PanelStatus;
  onRetry: () => void;
}

/** "Mike Trulove · Dr. Alandra W-B" / "Mike Trulove and 2 others". */
const expertLine = (embed: IEmbed): string => {
  const people = uniqBy(
    (embed.files || []).map((file) => file.videoPros?.[0]).filter(Boolean),
    "userid",
  );
  const names = people.map((p) => p?.displayName).filter(Boolean) as string[];
  if (names.length === 0) return "";
  if (names.length <= 2) return names.join(" · ");
  return `${names[0]} and ${names.length - 1} others`;
};

const coverFor = (embed: IEmbed): string | undefined => {
  const first = (embed.files || [])[0];
  if (!first) return undefined;
  const url = first.location || first.reviewS3File?.location || "";
  return url ? url.replace(".mp4", "_thumbnail.png") : undefined;
};

export const StoriesList = ({ stories, count, status, onRetry }: Props) => {
  const navigate = useNavigate();
  const { p } = usePortalPath();

  const tiles = useMemo(
    () =>
      stories.map((embed) => ({
        id: embed.id,
        title: embed.name,
        cover: coverFor(embed),
        subtitle: expertLine(embed),
        videoCount: (embed.files || []).length,
      })),
    [stories],
  );

  const body = () => {
    if (status === "loading") return <PanelSkeleton variant="stories" />;
    if (status === "error") {
      return (
        <PanelError
          icon={<IconStoryGlyph size={24} strokeWidth={1.6} />}
          title="Couldn't load stories"
          onRetry={onRetry}
          grow
        />
      );
    }
    if (tiles.length === 0) {
      return (
        <PanelEmpty
          icon={<IconStoryGlyph size={24} strokeWidth={1.6} />}
          title="Not in a story yet"
          body="Stories that include this video will appear here."
          grow
        />
      );
    }
    return (
      <div className="flex flex-col gap-3">
        {tiles.map((tile) => (
          <StoryTile
            key={tile.id}
            cover={tile.cover}
            title={tile.title}
            subtitle={tile.subtitle}
            videoCount={tile.videoCount}
            onClick={() => navigate(p(`/videos/embeds?embed=${tile.id}`))}
          />
        ))}
      </div>
    );
  };

  return (
    <VideoPanel
      id="panel-stories"
      title="Stories"
      count={status === "ready" ? count : undefined}
      fill
    >
      {body()}
    </VideoPanel>
  );
};

export default StoriesList;
